import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { FlexColumn } from '@/atoms/FlexContainers'
import { AsH2, Text } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { TheatricalReleaseResult } from '@/services/Theatrical'
import { useSafeAnalytics } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { EnhancedShowtimeProvider } from '@/views/TicketCheckoutViews/ShowtimesView/components/EnhancedShowtimeContext'
import ExclusiveShowtimeDateWarning from '@/views/TicketCheckoutViews/ShowtimesView/components/ExclusiveShowtimeDateWarning'
import VenueList, { LocaleWarningModalState } from '@/views/TicketCheckoutViews/ShowtimesView/components/VenueList'
import VenueListSkeleton from '@/views/TicketCheckoutViews/ShowtimesView/components/VenueListSkeleton'
import { GroupTicketsModal } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/GroupTicketsModal'
import LocalWarningModal from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/LocalWarningModal'
import { useTheatricalPromotionContext } from '../../components/TheatricalPromotionContext'
import useSortedVenues from '../hooks/useSortedVenues'
import { ShowtimesVenue } from '../hooks/useVenueSearch/useVenueSearch'
import { InitializedReservation } from '../initReservation'
import { SingAlongFilter } from './SingAlongFilter'
import { useTicketsContext } from './TicketsContext'
import VenuesSoldOutAlert from './VenuesSoldOutAlert'

interface Props {
  venues: ShowtimesVenue[]
  theatricalRelease: TheatricalReleaseResult
  isLoading: boolean
  reservation: InitializedReservation
  isExclusiveDateSelected?: boolean
}

const SUPPRESSED_EMPTY_STATE_SLUGS = [slugs.youngWashington, slugs.animalFarm]

const shouldSuppressEmptyState = (theatricalSlug: string | undefined, hasUnlimitedShowtimes: boolean) => {
  if (!theatricalSlug || !hasUnlimitedShowtimes) return false
  return SUPPRESSED_EMPTY_STATE_SLUGS.includes(theatricalSlug)
}

const Venues: FC<Props> = ({ venues, theatricalRelease, isLoading, reservation, isExclusiveDateSelected }) => {
  const { t } = useTranslate('tickets')
  const { isReady, query } = useSafeRouter()
  const [isGroupTicketsModalOpen, setIsGroupTicketsModalOpen] = useState(false)
  const soldOut =
    theatricalRelease?.soldOutShowtimeCount && theatricalRelease.soldOutShowtimeCount > 0
      ? theatricalRelease.soldOutShowtimeCount
      : undefined
  const { track } = useSafeAnalytics()
  const reservationId = reservation?.id
  const { promo, experiments } = useTheatricalPromotionContext()
  const { releaseTypeFilter } = useTicketsContext()
  const isSingAlongFilterEnabled = !!experiments?.['david-sing-along-filter']
  const isBasicPageNoShowtimesExperiment = Boolean(experiments?.['basic-page-no-showtimes-v2'])

  const theatricalSlug = theatricalRelease?.theatricalSlug

  const { venuesWithShowtimes, venuesWithNoShowtimes } = useSortedVenues({
    venues,
    releaseTypeFilter,
  })

  const openGroupTicketsModal = useCallback(() => {
    setIsGroupTicketsModalOpen(true)
  }, [])

  useEffect(() => {
    if (query?.highlightGroupTickets || query?.groupDiscountCode) {
      openGroupTicketsModal()
    }
  }, [openGroupTicketsModal, query?.highlightGroupTickets, query?.groupDiscountCode])

  const [localeWarningModalState, setLocaleWarningModalState] = useState<LocaleWarningModalState>({
    isOpen: false,
    warning: '',
    callback: () => null,
  })

  const closeLocaleWarningModal = useCallback(() => {
    track('Canceled Showtime From Language Modal')
    setLocaleWarningModalState({ isOpen: false, warning: '', callback: () => null })
  }, [track])

  const mainPromoIsGuild = promo?.promoType === 'premium_guild'

  const hasUnlimitedShowtimes = venuesWithNoShowtimes?.some((venue) => venue?.isPresaleCompatible)
  const shouldHideEmptyState = useMemo(() => {
    return (
      isLoading ||
      !isReady ||
      venuesWithShowtimes?.length > 0 ||
      shouldSuppressEmptyState(theatricalSlug, hasUnlimitedShowtimes)
    )
  }, [venuesWithShowtimes, isLoading, isReady, theatricalSlug, hasUnlimitedShowtimes])

  if (!theatricalSlug) return null
  if (isLoading || !isReady) return <VenueListSkeleton />

  return (
    <EnhancedShowtimeProvider venues={venues}>
      <FlexColumn className="min-h-[50vh] gap-6 md:min-h-[55vh]">
        <FlexColumn className="gap-4">
          {isSingAlongFilterEnabled && <SingAlongFilter theatricalRelease={theatricalRelease} />}
          {isExclusiveDateSelected && <ExclusiveShowtimeDateWarning theatricalSlug={theatricalSlug} />}

          {soldOut && soldOut > 9 && venuesWithShowtimes?.length > 0 && <VenuesSoldOutAlert soldOut={soldOut} />}

          <VenueList
            venues={venuesWithShowtimes}
            theatricalRelease={theatricalRelease}
            theaterSlug={theatricalSlug}
            setLocaleWarningModalState={setLocaleWarningModalState}
            isGroupTicketsModalOpen={isGroupTicketsModalOpen}
            openGroupTicketsModal={openGroupTicketsModal}
            reservationId={reservationId}
            theatricalSlug={theatricalSlug}
            showGuildTicketBanner={!mainPromoIsGuild}
            showGiveawayEmptyState
            shouldHideEmptyState={shouldHideEmptyState}
          />
        </FlexColumn>

        {venuesWithNoShowtimes?.length > 0 && !isExclusiveDateSelected && (
          <FlexColumn
            className={classNames('gap-4', {
              'experiment-hidden': !isBasicPageNoShowtimesExperiment,
            })}
          >
            {!shouldHideEmptyState && (
              <Text as={AsH2} className="photon-title-md md:photon-title-lg" weight="bold">
                {t('noAvailableShowtimes', 'No Available Showtimes')}
              </Text>
            )}
            <VenueList
              venues={venuesWithNoShowtimes}
              theatricalRelease={theatricalRelease}
              theaterSlug={theatricalSlug}
              setLocaleWarningModalState={setLocaleWarningModalState}
              isGroupTicketsModalOpen={isGroupTicketsModalOpen}
              openGroupTicketsModal={openGroupTicketsModal}
              reservationId={reservationId}
              theatricalSlug={theatricalSlug}
              showGuildTicketBanner={Boolean(!venuesWithShowtimes?.length) && !mainPromoIsGuild}
              showGiveawayEmptyState={Boolean(!venuesWithShowtimes?.length)}
              className={isBasicPageNoShowtimesExperiment ? '!flex !flex-col' : ''}
            />
          </FlexColumn>
        )}
      </FlexColumn>
      <LocalWarningModal onClose={closeLocaleWarningModal} localeWarningModalState={localeWarningModalState} />
      {isGroupTicketsModalOpen && theatricalRelease?.groupProductVariantId && (
        <GroupTicketsModal
          theatricalSlug={theatricalSlug}
          open={isGroupTicketsModalOpen}
          onClose={() => setIsGroupTicketsModalOpen(false)}
          groupTicketProductVariantId={theatricalRelease.groupProductVariantId}
        />
      )}
    </EnhancedShowtimeProvider>
  )
}

export default Venues
